/**
 * Notes, self-contained the way `AreasCard` is — its own fetch of `/api/notes`, its own
 * areas/projects for `NoteFields`' destination picker, its own create/edit/delete.
 *
 * `NotesSection` stays the presentational component (the list, the add form, each row's own
 * edit/delete); this just feeds it data the way `Briefing.tsx` used to (`loadDestinations`
 * plus its own `addNote`/`editNote`/`removeNote`), so Notes can be mounted on any page.
 */

import { useCallback, useEffect, useState } from "react";
import { ApiError, api, type CaptureTier, type Note } from "../api";
import { useAreasAndProjects } from "../areasProjects";
import { refineNoteInPlace } from "../aiRefine";
import { useBulbTier } from "../bulbTier";
import { postNotice } from "../notices";
import { queueNoteCreate } from "../taskQueue";
import { NotesSection, type NoteEditValues } from "./NotesSection";

export function NotesCard({
  refreshKey,
  onChange,
}: {
  /** Bumped whenever something elsewhere changed, so a newly-touched area/project shows up
   * in the destination picker without this card needing to know why. */
  refreshKey?: number;
  /** Called after this card changes anything, so a sibling relying on the same
   * areas/projects (Areas, Structure, wherever it lives) knows to refetch too. */
  onChange?: () => void | Promise<void>;
}) {
  const { areas, projects } = useAreasAndProjects(refreshKey);
  const [notes, setNotes] = useState<Note[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { tier: noteTier } = useBulbTier("note-form");

  const load = useCallback(async () => {
    try {
      setNotes((await api.notes()).notes);
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.isAuth) return; // AuthGate takes over
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const notify = useCallback(async () => {
    await load();
    await onChange?.();
  }, [load, onChange]);

  const onCreate = useCallback(
    async (values: { title: string; body: string | null; area_id?: string; project_id?: string }) => {
      try {
        await api.createNote(values);
      } catch (err) {
        if (!(err instanceof ApiError && err.isOffline)) throw err;
        await queueNoteCreate(values);
      }
      await notify();
    },
    [notify],
  );

  const onCreateWithAi = useCallback(
    async (values: { title: string; body: string | null; area_id?: string; project_id?: string }) => {
      let answer;
      try {
        answer = await api.createNoteWithAi({ ...values, ...(noteTier ? { tier: noteTier } : {}) });
      } catch (err) {
        if (err instanceof ApiError && err.isOffline) {
          // The note is made when the AI answers, so there is nothing to hold for later.
          throw new Error("Asking the AI needs a connection. Try again once you're back online.");
        }
        throw err;
      }
      // The form closes either way: a failed note is kept (in the Inbox, as a note), and leaving
      // the text in the form would invite saving it a second time.
      postNotice(
        answer.created
          ? `Created Note: ${answer.created.title}`
          : `"${values.title}" is waiting in the Inbox. ${answer.error ?? "The AI didn't answer."}`,
      );
      await notify();
    },
    [noteTier, notify],
  );

  const onEdit = useCallback(
    async (id: string, patch: Record<string, unknown>) => {
      await api.patchNote(id, patch);
      await notify();
    },
    [notify],
  );

  const onEditWithAi = useCallback(
    async (id: string, values: NoteEditValues, tier: CaptureTier | null) => {
      const refined = await refineNoteInPlace(id, { ...values, ...(tier ? { tier } : {}) });
      await notify();
      return refined;
    },
    [notify],
  );

  const onDelete = useCallback(
    async (id: string) => {
      await api.deleteNote(id);
      await notify();
    },
    [notify],
  );

  return (
    <>
      {error && (
        <p className="error" style={{ margin: "0 0 0.6rem" }}>
          {error}
        </p>
      )}
      <NotesSection
        notes={notes}
        areas={areas}
        projects={projects}
        onCreate={onCreate}
        onCreateWithAi={onCreateWithAi}
        onEdit={onEdit}
        onEditWithAi={onEditWithAi}
        onDelete={onDelete}
      />
    </>
  );
}
